import { VariantType } from '@/components/sections/builder/elements/body/product-detail/variant-picker';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { ThemeColorSchemeType, ThemeVariantPillType } from '@/stores/builder';
import { useCartProduct } from '@/stores/cart';
import { IProduct, ProductItem } from '@/types/product';
import { shallow } from 'zustand/shallow';

type PillsPickerProps = {
  product: IProduct;
  variants: VariantType[];
  items: ProductItem[];
  selectedOptions: Record<string, string>;
  handleSelect: (variantId: string, optionId: string) => void;
  theme?: ThemeVariantPillType;
  colorScheme?: ThemeColorSchemeType;
};

export const PillsPicker = ({
  product,
  variants,
  items,
  selectedOptions,
  handleSelect,
  theme,
  colorScheme,
}: PillsPickerProps) => {
  const cartStore = useCartProduct(
    (state) => ({
      setQuantity: state.setQuantity,
    }),
    shallow
  );
  const availableQty = product.available_qty as number;
  const hasColorScheme = Boolean(colorScheme);

  return (
    <>
      <div>
        {variants.map((variant) => (
          <div key={variant.variant_id}>
            <span className='text-sm'>{variant.label}</span>
            <ToggleGroup
              type='single'
              className={cn(
                'mb-4 mt-3 w-fit flex-wrap items-start justify-start',
                'theme_variant_pill_group'
              )}
              defaultValue={selectedOptions[variant.variant_id]}
              value={selectedOptions[variant.variant_id]}
              disabled={availableQty === 0}
            >
              {variant.options.map((option) => {
                const isSelected = selectedOptions[variant.variant_id] === option.option_id;

                const tempOptions = { ...selectedOptions };
                if (isSelected) {
                  delete tempOptions[variant.variant_id];
                } else {
                  tempOptions[variant.variant_id] = option.option_id;
                }

                const tempSelectedOption = Object.fromEntries(
                  Object.entries(tempOptions).filter(([_, v]) => v)
                );

                const tempAllSelected = Object.keys(tempSelectedOption).length === variants.length;

                let isOutOfStock = false;
                if (tempAllSelected) {
                  const selectedKey = Object.values(tempSelectedOption).join(':');
                  const stock = items.find((item) => item.item_option_id === selectedKey)?.stock;
                  isOutOfStock = stock === undefined || stock === 0;
                }

                const disabled = tempAllSelected ? isOutOfStock && !isSelected : false;

                const handleChange = () => {
                  if (disabled) return;
                  handleSelect(variant.variant_id, isSelected ? '' : option.option_id);
                  cartStore.setQuantity(1);
                };

                return (
                  <ToggleGroupItem
                    key={option.option_id}
                    className={cn(
                      'min-w-[4.8em] px-2 text-sm disabled:cursor-not-allowed',
                      hasColorScheme ? 'theme_variant_pill_product_detail' : 'theme_variant_pill',
                      disabled && 'relative overflow-hidden bg-gray-200 text-gray-400'
                    )}
                    onClick={handleChange}
                    value={option.option_id}
                    disabled={disabled}
                  >
                    {disabled && !isSelected && (
                      <div
                        className='absolute inset-0 bg-gray-300 bg-[repeating-linear-gradient(135deg,transparent,transparent_10px,rgba(0,0,0,0.1)_10px,rgba(0,0,0,0.1)_20px)] opacity-30'
                        aria-hidden='true'
                      ></div>
                    )}
                    <div
                      className={cn(
                        'break-all text-sm',
                        disabled && !isSelected && 'text-muted-foreground',
                        isSelected &&
                          hasColorScheme &&
                          'theme_variant_pill_product_detail_selected',
                        isSelected && !hasColorScheme && 'theme_variant_pill_selected'
                      )}
                    >
                      {option.value}
                    </div>
                  </ToggleGroupItem>
                );
              })}
            </ToggleGroup>
          </div>
        ))}
      </div>

      <style jsx global>
        {`
          .theme_variant_pill_product_detail {
            outline: ${theme?.borderThickness}px solid ${colorScheme?.btnOutline || '#52535B'} !important;
            border-radius: ${theme?.borderCornerRadius}px !important;
            min-height: 50px;
            margin-right: ${(theme?.borderThickness ?? 0) * 2 + 8}px;
            border: ${theme?.borderThickness} solid transparent;
            box-shadow: ${theme?.shadowHorizontalOffset}px ${theme?.sahdowVerticalOffset}px
              ${theme?.shadowBlur}px rgba(0, 0, 0, ${(theme?.shadowOpacity ?? 0) / 100}) !important;
            webkit-box-shadow: ${theme?.shadowHorizontalOffset}px ${theme?.sahdowVerticalOffset}px
              ${theme?.shadowBlur}px rgba(0, 0, 0, ${(theme?.shadowOpacity ?? 0) / 100}) !important;
            moz-box-shadow: ${theme?.shadowHorizontalOffset}px ${theme?.sahdowVerticalOffset}px
              ${theme?.shadowBlur}px rgba(0, 0, 0, ${(theme?.shadowOpacity ?? 0) / 100}) !important;
            color: ${colorScheme?.text || '#52535B'} !important;
            background-color: ${colorScheme?.background || 'white'} !important;
          }
          .theme_variant_pill_product_detail[data-state='on'] {
            border: 0px;
            color: ${colorScheme?.btnSolidlabel || '#52535B'} !important;
            background-color: ${colorScheme?.btnSolidBackgroud || 'white'} !important;
          }
          .theme_variant_pill_product_detail_selected {
            color: ${colorScheme?.btnSolidlabel || '#52535B'} !important;
            background-color: ${colorScheme?.btnSolidBackgroud || 'white'} !important;
          }
        `}
      </style>
    </>
  );
};
